import React, { useEffect, useRef, useState } from "react"

import { GripVertical, Plus, Trash2 } from "lucide-react"

import { cn } from "@/utils/cn"

// ─────────────────────────────────────────────────────────────────────────────
// DraggableListEditor
//
// Shared edit-mode list editor extracted from TestCaseBehaviorsCell and
// MetadataColumnCell's ListCell. Owns the mechanics both needed identically:
//   • add / delete rows
//   • drag-to-reorder with top/bottom drop indicators
//   • focusing a newly added row
//
// Differences between the two call sites are handled via props:
//   • the per-row control is supplied by `renderItem` (a plain <textarea> for
//     metadata, an <ExpandableTemplateVariableText> for behaviors)
//   • metadata defers committing a freshly-added empty row until the first
//     keystroke (`commitOnAdd={false}`); behaviors commits it immediately
//   • metadata wants in-editor Enter-to-add / Backspace-to-delete; behaviors
//     handles those at the cell level, so it omits `onKeyDown` from its row
//
// The editor is generic over the focusable element type (`E`) so behaviors can
// focus a wrapper <div> while metadata focuses the <textarea> directly.
// ─────────────────────────────────────────────────────────────────────────────

interface DraggableListItemRenderProps<
  E extends HTMLElement = HTMLTextAreaElement,
> {
  /** Current value of this row. */
  value: string
  /** Row index. */
  index: number
  /** Update this row's value. */
  onChange: (value: string) => void
  /** Enter-to-add / Backspace-on-empty-to-delete handler. Callers that handle
   *  keyboard elsewhere (e.g. behaviors, at the cell level) can ignore this. */
  onKeyDown: (e: React.KeyboardEvent<E>) => void
  /** Ref callback the caller MUST attach to its focusable element so the editor
   *  can focus newly added rows. */
  registerRef: (el: E | null) => void
}

interface DraggableListEditorProps<
  E extends HTMLElement = HTMLTextAreaElement,
> {
  /** Source of truth for the rows. */
  items: string[]
  /** Called whenever the committed list changes (add/delete/edit/reorder). */
  onItemsChange: (items: string[]) => void
  /** Renders the editable control for a single row. */
  renderItem: (props: DraggableListItemRenderProps<E>) => React.ReactNode
  /** Label for the trailing add button, e.g. "item" or "behavior". */
  addLabel?: string
  /** aria-label / title noun for the delete button, e.g. "item" or "behavior". */
  deleteLabel?: string
  /** Commit edits on every keystroke. Default true. */
  commitOnChange?: boolean
  /** Commit immediately when a new (empty) row is added. Metadata defers this to
   *  the first keystroke (false); behaviors commits the empty row (true).
   *  Default false. */
  commitOnAdd?: boolean
  /** Whether the editor focuses the element returned via registerRef and places
   *  the caret at the end. Disable for elements that manage their own focus.
   *  Default true. */
  selectOnFocus?: boolean
}

export function DraggableListEditor<
  E extends HTMLElement = HTMLTextAreaElement,
>({
  items: externalItems,
  onItemsChange,
  renderItem,
  addLabel = "item",
  deleteLabel = "item",
  commitOnChange = true,
  commitOnAdd = false,
  selectOnFocus = true,
}: DraggableListEditorProps<E>) {
  const [items, setItems] = useState<string[]>(externalItems)
  const [focusIndex, setFocusIndex] = useState<number | null>(null)
  const itemRefs = useRef<(E | null)[]>([])

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const draggingIndexRef = useRef<number | null>(null)
  const dragOverIndexRef = useRef<number | null>(null)

  // Re-sync when the external value changes (undo, remote update, etc.) — but
  // never stomp the row the user is mid-edit on. Callers may normalize the
  // committed list (e.g. MetadataColumnCell drops empty rows), so the value
  // echoing back can differ from local state only by the empty entry being
  // edited. Adopt the external value only when it's a genuine outside change,
  // not just that normalized echo of what we already hold locally.
  const prevSerializedRef = useRef(JSON.stringify(externalItems))
  const nextSerialized = JSON.stringify(externalItems)
  if (nextSerialized !== prevSerializedRef.current) {
    prevSerializedRef.current = nextSerialized
    const localWithoutEmpties = JSON.stringify(
      items.filter((i) => i.trim() !== ""),
    )
    if (nextSerialized !== localWithoutEmpties) {
      setItems(externalItems)
    }
  }

  // Focus a newly added row once it renders.
  useEffect(() => {
    if (focusIndex !== null) {
      const el = itemRefs.current[focusIndex]
      if (el) {
        el.focus()
        if (
          selectOnFocus &&
          (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement)
        ) {
          el.selectionStart = el.selectionEnd = el.value.length
        }
        setFocusIndex(null)
      }
    }
  }, [focusIndex, items.length, selectOnFocus])

  const handleChange = (index: number, val: string) => {
    const next = items.map((item, i) => (i === index ? val : item))
    setItems(next)
    if (commitOnChange) {
      onItemsChange(next)
    }
  }

  const handleDelete = (index: number) => {
    const next = items.filter((_, i) => i !== index)
    setItems(next)
    onItemsChange(next)
    if (next.length > 0) {
      setFocusIndex(Math.min(index, next.length - 1))
    }
  }

  const handleAdd = () => {
    const newIndex = items.length
    const next = [...items, ""]
    setItems(next)
    setFocusIndex(newIndex)
    if (commitOnAdd) {
      onItemsChange(next)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<E>, index: number) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleAdd()
    }
    if (e.key === "Backspace" && items[index] === "") {
      e.preventDefault()
      handleDelete(index)
    }
  }

  // ── Drag helpers ──────────────────────────────────────────────────────────
  const handleDragStart = (index: number) => {
    draggingIndexRef.current = index
    setDraggingIndex(index)
  }

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    const target = e.clientY < midY ? index : index + 1
    dragOverIndexRef.current = target
    setDragOverIndex(target)
  }

  const handleDrop = () => {
    const from = draggingIndexRef.current
    const to = dragOverIndexRef.current
    draggingIndexRef.current = null
    dragOverIndexRef.current = null
    setDraggingIndex(null)
    setDragOverIndex(null)
    if (from === null || to === null || from === to) {
      return
    }
    const reordered = [...items]
    const [moved] = reordered.splice(from, 1)
    const insertAt = from < to ? to - 1 : to
    reordered.splice(insertAt, 0, moved)
    setItems(reordered)
    onItemsChange(reordered)
  }

  const handleDragEnd = () => {
    draggingIndexRef.current = null
    dragOverIndexRef.current = null
    setDraggingIndex(null)
    setDragOverIndex(null)
  }

  return (
    <div
      className="flex flex-col gap-2 w-full min-w-0"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.stopPropagation()
        if (dragOverIndexRef.current === null) {
          dragOverIndexRef.current = items.length
        }
        handleDrop()
      }}
    >
      {items.map((item, index) => {
        const isDragging = draggingIndex === index
        const isLastItem = index === items.length - 1
        const showTopBorder = dragOverIndex === index && draggingIndex !== index
        const showBottomBorder =
          isLastItem && dragOverIndex === items.length && draggingIndex !== null

        return (
          <div
            key={`list-item-${index}`}
            className={cn(
              "flex gap-1 items-start group transition-opacity",
              isDragging ? "opacity-40" : "opacity-100",
              showTopBorder
                ? "border-t-2 border-coval-border-active pt-0.5"
                : "",
              showBottomBorder
                ? "border-b-2 border-coval-border-active pb-0.5"
                : "",
            )}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => {
              e.stopPropagation()
              handleDrop()
            }}
            onDragEnd={handleDragEnd}
          >
            {/* Drag handle */}
            <div className="flex-shrink-0 mt-2 cursor-grab active:cursor-grabbing text-coval-text-muted hover:text-coval-text transition-colors">
              <GripVertical className="w-4 h-4" />
            </div>

            {/* Item input card */}
            <div className="flex-1 min-w-0 bg-surface-1 rounded p-2 hover:bg-surface-2 transition-colors min-h-8">
              {renderItem({
                value: item,
                index,
                onChange: (val) => handleChange(index, val),
                onKeyDown: (e) => handleKeyDown(e, index),
                registerRef: (el) => {
                  itemRefs.current[index] = el
                },
              })}
            </div>

            {/* Delete — hidden until hover */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleDelete(index)
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-coval-danger-text hover:text-coval-danger-text p-1 mt-1"
              title={`Delete ${deleteLabel}`}
              aria-label={`Delete ${deleteLabel} ${index + 1}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )
      })}

      {/* "+ <label>" footer */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          handleAdd()
        }}
        className="w-full px-2 py-2 text-sm outline-none text-coval-text-muted hover:text-coval-text transition-colors text-left"
      >
        <span className="inline-flex items-center gap-1">
          <Plus className="w-4 h-4" />
          {addLabel}
        </span>
      </button>
    </div>
  )
}
